import * as Sentry from '@sentry/react';
import 'whatwg-fetch';

import { redirect } from 'containers/App/actions';
import { setGlobalMessage } from 'containers/MessageHandler/actions';
import localStorageUser from 'utils/localStorageUser';
import sessionStorageUser from 'utils/sessionStorageUser';

let isRefreshing = false;
let refreshQueue = [];

const processQueue = (error, token = null) => {
  refreshQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  refreshQueue = [];
};

function checkErrorStatus(error) {
  return (
    error?.response?.status &&
    (error.response.status === 400 ||
      error.response.status === 405 ||
      (error.response.status !== 410 &&
        error.response.status >= 407 &&
        error.response.status <= 502) ||
      (error.response.status >= 504 && error.response.status <= 510) ||
      (error.response.status >= 512 && error.response.status < 600))
  );
}

function replaceUUIDInURL(url) {
  // Split the URL by '?' to separate the base URL from the parameters
  const [baseUrl] = url.split('?');

  // Replace the UUID in the base URL with '<uuid>'
  return baseUrl
    .replace(
      /\/[0-9a-fA-F]{8}\b-[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/g,
      '/<uuid>',
    )
    .replace(
      /\/[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/g,
      '/<uuid>',
    );
}

/**
 * Returns the thrown error message
 *
 * @param  {object} Error response from a network request
 * @param  {errorCustom} Bool do not catch the error
 *
 * @return {object} The constructed Error Message from the request
 */
async function handleError(error, errorCustom = false, params = {}) {
  let processedErrorResponse;
  let message;

  try {
    if (error && error.response && typeof error.response.json === 'function') {
      processedErrorResponse = await error.response.json();
    }
  } catch (e) {
    processedErrorResponse = null;
  }

  if (processedErrorResponse) {
    message = processedErrorResponse.message || processedErrorResponse.Message;
  } else {
    message = error.message || error.Message;
  }
  if (!errorCustom && error?.response?.status && error.response.status === 403) {
    // 403 Forbidden
    window.finalcadReduxStore.dispatch(
      setGlobalMessage({
        type: 'error',
        active: true,
        intlMessage: {
          id: 'forbidden_error',
        },
        contactSupport: true,
        verticalPosition: 'top',
        horizontalPosition: 'center',
      }),
    );
  }
  if (!errorCustom && checkErrorStatus(error)) {
    // if error status is 400, 405, 407-502, 504-510, 512-599
    window.finalcadReduxStore.dispatch(
      setGlobalMessage({
        type: 'error',
        active: true,
        intlMessage: {
          id: 'error_refresh_contact_support',
        },
        contactSupport: true,
        verticalPosition: 'top',
        horizontalPosition: 'center',
      }),
    );
  } else if (error?.response?.status === 503) {
    // 503 Service Unavailable
    window.finalcadReduxStore.dispatch(
      setGlobalMessage({
        type: 'error',
        active: true,
        intlMessage: {
          id: 'services_under_maintenance',
        },
        contactSupport: true,
        verticalPosition: 'top',
        horizontalPosition: 'center',
      }),
    );
  } else if (error?.response?.status === 511) {
    // 511 Network Authentication Required
    window.finalcadReduxStore.dispatch(
      setGlobalMessage({
        type: 'error',
        active: true,
        intlMessage: {
          id: 'error_login_expired',
          values: { id: error.response.status },
        },
        contactSupport: true,
        verticalPosition: 'top',
        horizontalPosition: 'center',
      }),
    );
  }
  Sentry.withScope(scope => {
    scope.setTag('network', 'fetch_error');

    const fingerprintUrl = replaceUUIDInURL(params.url);

    scope.setFingerprint(['{{ default }}', fingerprintUrl, params.request.method]);
    scope.setContext('Request details', {
      errorStatus: error?.response?.status,
      message,
      referer: window.location.href,
      url: params.url,
      method: params.request.method,
      body: params.request.body,
      headers: { ...params.request.headers },
    });
    Sentry.captureException(error);
  });

  return {
    status: error && error.response ? error.response.status : null,
    response: processedErrorResponse,
    message,
  };
}

const logoutAndRedirect = () => {
  sessionStorageUser.setFinalcadTempRegistrationInfo(null);
  sessionStorageUser.setIsSignUp(null);
  if (window.finalcadAuthSession?.logout) {
    window.finalcadAuthSession.logout();
  } else {
    localStorageUser.removeAuthTokens();
    if (window.finalcadHistory?.replace) {
      window.finalcadHistory.replace('/login');
    }
  }
};

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 */
async function checkStatus(
  response,
  url,
  options,
  errorRedirect = true,
  redirectionUrl,
  isRetry = false,
) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  const authTokens = localStorageUser.getAuthTokens();

  if (response.status === 401 && authTokens) {
    // eslint-disable-next-line no-use-before-define
    return handleUnauthorized(response, url, options, errorRedirect, redirectionUrl, isRetry);
  }

  // Standard errors (403, 404, etc.).
  if (errorRedirect && response.status === 403) {
    window.finalcadReduxStore.dispatch(redirect(`/${response.status}`));
  }
  if (
    errorRedirect &&
    (response.status === 401 || response.status === 404) &&
    redirectionUrl !== undefined
  ) {
    window.finalcadReduxStore.dispatch(redirect(`/${redirectionUrl}`));
  }

  const error = new Error(response.statusText);
  error.response = response;
  throw error;
}

async function handleUnauthorized(response, url, options, errorRedirect, redirectionUrl, isRetry) {
  if (!isRetry) {
    // First 401 for this request: retry the same flow with the retry flag set.
    return checkStatus(response, url, options, errorRedirect, redirectionUrl, true);
  }

  // Fallback when the auth session is not ready: just sign out.
  if (typeof window.finalcadAuthSession?.fetchAccessToken !== 'function') {
    logoutAndRedirect();
    const error = new Error(response.statusText);
    error.response = response;
    throw error;
  }

  // Reserve the refresh slot atomically before any await.
  const wasRefreshing = isRefreshing;
  if (!wasRefreshing) isRefreshing = true;

  // A refresh is already in progress: queue and replay once the new token is available.
  if (wasRefreshing) {
    return new Promise((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    }).then(newToken => {
      const retryOptions = { ...options };
      retryOptions.headers = { ...retryOptions.headers, Authorization: `Bearer ${newToken}` };
      return window
        .fetch(url, retryOptions)
        .then(res => checkStatus(res, url, retryOptions, errorRedirect, redirectionUrl, true));
    });
  }

  // First request to hit a 401: trigger the refresh and replay this request.
  try {
    const newToken = await window.finalcadAuthSession.fetchAccessToken({ forceRefresh: true });
    if (!newToken) {
      // e.g. refresh token expired; throw to enter the catch block below.
      throw new Error('No new token provided');
    }
    isRefreshing = false;
    processQueue(null, newToken);

    const retryOptions = { ...options };
    retryOptions.headers = { ...retryOptions.headers, Authorization: `Bearer ${newToken}` };
    const retryResponse = await window.fetch(url, retryOptions);
    return checkStatus(retryResponse, url, retryOptions, errorRedirect, redirectionUrl, true);
  } catch (err) {
    // Renewal failed (dead refresh token, etc.).
    isRefreshing = false;
    processQueue(err, null);
    logoutAndRedirect();
    const error = new Error(response.statusText);
    error.response = response;
    throw error;
  }
}

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 * @param params
 * @param noHeaders
 *
 * @return {object}           The response data
 */
export async function request(url, options, params = {}, noHeaders) {
  const { errorCustom = false, errorRedirect = true, redirectionUrl } = params;
  const updatedOptions = { ...options };
  const accessToken = localStorageUser.getAccessToken();
  const lang = localStorageUser.getLanguageUserFromLocalStorage();

  if (!noHeaders) {
    updatedOptions.headers = {
      'Cache-Control': 'No-Store',
      'Accept-Language': lang,
      'Access-Control-Allow-Origin': '*',
      lang: `${lang}`,
      ...(options.headers || {}),
    };
    if (accessToken) {
      updatedOptions.headers = {
        Authorization: `Bearer ${accessToken}`,
        ...updatedOptions.headers,
      };
    }
  }

  if (options.headers?.Authorization === 'disable') {
    delete updatedOptions.headers.Authorization;
  }

  try {
    const response = await window.fetch(url, updatedOptions);
    const checkedResponse = await checkStatus(
      response,
      url,
      updatedOptions,
      errorRedirect,
      redirectionUrl,
    );
    const text = await checkedResponse.text();
    return text ? JSON.parse(text) : {};
  } catch (e) {
    return handleError(e, errorCustom, { url, request: updatedOptions, params });
  }
}

export const requestFile = async (url, options, params = {}) => {
  const { errorCustom = false, errorRedirect = true } = params;

  const lang = localStorageUser.getLanguageUserFromLocalStorage();
  const updatedOptions = { ...options };
  const accessToken = localStorageUser.getAccessToken();
  updatedOptions.headers = {
    'Access-Control-Allow-Origin': '*',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    'Accept-Language': lang,
    ...(options.headers || {}),
  };
  try {
    const response = await window.fetch(url, updatedOptions);
    return await checkStatus(response, url, updatedOptions, errorRedirect);
  } catch (e) {
    return handleError(e, errorCustom, { url, request: updatedOptions, params });
  }
};

export const searchParams = params =>
  Object.keys(params)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&');

const processRequestWithPayload = (urlParams, requestURL, url, method, payload) => {
  let requestURLCopy = requestURL;
  const urlParamsCopy = urlParams;
  if (urlParams?.offset !== undefined && urlParams?.limit) {
    requestURLCopy = `${url}?offset=${urlParams.offset}&limit=${urlParamsCopy.limit}`;
    delete urlParamsCopy.offset;
    delete urlParamsCopy.limit;
    if (urlParams?.business_organization_id) {
      requestURLCopy = `${requestURLCopy}&business_organization_id=${
        urlParamsCopy?.business_organization_id
      }`;
      delete urlParamsCopy.business_organization_id;
    }
  } else if (urlParamsCopy?.business_organization_id) {
    requestURLCopy = `${requestURL}?business_organization_id=${
      urlParamsCopy?.business_organization_id
    }`;
    delete urlParamsCopy.business_organization_id;
  }
  const requestOptions = {
    method,
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'No-Store',
    },
    body: JSON.stringify(payload),
  };
  return { requestURLCopy, requestOptions };
};

export const parseRequest = params => {
  const { url, urlParams, method /* , user */ } = params || {};
  let requestURL = `${url}`;
  const payload = {
    ...urlParams,
  };
  let options;
  if ((method === 'GET' || method === 'DELETE') && urlParams) {
    let requestPayloadString = '';
    Object.keys(urlParams).forEach((key, index) => {
      if (index) {
        requestPayloadString += `&${key}=${urlParams[key]}`;
      } else {
        requestPayloadString += `${key}=${urlParams[key]}`;
      }
    });
    requestURL = `${requestURL}`;
    if (requestPayloadString) {
      requestURL = `${requestURL}?${requestPayloadString}`;
    }
    options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'No-Store',
      },
    };
  } else if ((method === 'GET' || method === 'DELETE') && !urlParams) {
    requestURL = `${requestURL}`;
    options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'No-Store',
      },
    };
  } else {
    const { requestURLCopy, requestOptions } = processRequestWithPayload(
      urlParams,
      requestURL,
      url,
      method,
      payload,
    );
    requestURL = requestURLCopy;
    options = {
      ...requestOptions,
    };
  }
  return { requestURL, options };
};

export const getNameFromContentDisposition = headers => {
  let name = headers
    .get('content-disposition')
    .split(';')
    .find(n => n?.includes('filename='))
    .replace('filename=', '')
    .trim();

  if (name?.charAt(0) === '"' && name?.charAt(name.length - 1) === '"') {
    name = name?.slice(1, -1);
  }

  return name;
};

export function prepareFCHeader(prepare = headers => headers) {
  const lang = localStorageUser.getLanguageUserFromLocalStorage();
  return (headers, { getState }) => {
    const accessToken = localStorageUser.getAccessToken();

    if (!headers.has('x-skip-content-type')) {
      headers.set('content-type', 'application/json');
    }

    headers.set('Cache-Control', 'No-Store');
    headers.set('Accept-Language', lang);

    if (accessToken && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
    return prepare(headers, { getState });
  };
}

export default request;
